// Copyright (c) 2026 Dyna Robotics, Inc. All rights reserved.
// Dyna Robotics Proprietary and Confidential.
// Unauthorized copying, use, or distribution is prohibited.
// SPDX-License-Identifier: LicenseRef-Dyna-Internal

/**
 * Product types (design doc §7.3, §9.2, US-E-01).
 *
 * The create form says in plain words that a type cannot be edited afterwards, because the
 * archive prefix is baked into every archive number the type will ever issue (§7.4.1) —
 * changing it later would split one product's machines across two numbering schemes.
 * Discontinuing is the way out: it stops new filings while every existing archive keeps
 * resolving.
 *
 * Delete is on every row but greyed out once an archive has been filed; hovering it says
 * why — with the number, and with the thing to do instead — rather than letting the
 * operator find out from a 409. The confirmation dialog, for the rows it is open to, says
 * which unused BOMs go with the type.
 */

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import {
  ARCHIVE_PREFIX_PATTERN,
  ARCHIVE_PREFIX_PATTERN_SOURCE,
  DEVICE_NO_REGEX_MAX_LEN,
  NAME_MAX_LEN,
} from "@/api/constraints";
import { api } from "@/api/endpoints";
import type { ProductTypeListItem } from "@/api/types";
import { AddLabel } from "@/components/AddLabel";
import { ErrorBanner } from "@/components/ErrorBanner";
import { IconButton } from "@/components/IconButton";
import { BanIcon, RestoreIcon, TrashIcon } from "@/components/icons";
import { Modal } from "@/components/Modal";
import { Pager } from "@/components/Pager";
import { ResizableTable } from "@/components/ResizableTable";
import { StatusBadge } from "@/components/StatusBadge";
import { useCursorList } from "@/hooks/useCursorList";
import { usePermissions } from "@/hooks/usePermissions";

const EMPTY_FORM = {
  name: "",
  has_bom: true,
  archive_prefix: "",
  device_no_regex: "",
  sn_type_code: "",
};

/** A type's two characters in a robot SN (backend domains/archive/robot_sn.py). */
const SN_TYPE_CODE_PATTERN = /^[A-Z0-9]{2}$/;
const SECOND_CHARACTERS = "1234567890ABCDEFGHIJKLMNOPQRSTUVWXYZ";

/**
 * The code a new type is offered: its archive prefix's first letter and the first free
 * character after it (`RBT` → `R1`, or `R2` when `R1` is taken) — the rule the server
 * applies to a type created without one. Free among the types listed; the server's
 * uniqueness check has the last word.
 */
export function defaultSnTypeCode(archivePrefix: string, taken: Set<string>): string {
  const letter = archivePrefix.slice(0, 1).toUpperCase();
  if (!letter) return "";
  for (const second of SECOND_CHARACTERS) {
    if (!taken.has(letter + second)) return letter + second;
  }
  return "";
}

export function ProductTypes() {
  const { t } = useTranslation();
  // Read by everyone; created, stopped and deleted by an administrator (§12.4).
  const { canManageMasterData } = usePermissions();
  const queryClient = useQueryClient();
  const [form, setForm] = useState(EMPTY_FORM);
  const [creating, setCreating] = useState(false);
  // The whole row, not just its id: the dialog needs the name to name what it is about
  // and the counts to say what deleting takes with it.
  const [pendingDelete, setPendingDelete] = useState<ProductTypeListItem | null>(null);
  // Filled in from the prefix until the operator types a code of their own.
  const [snCodeEdited, setSnCodeEdited] = useState(false);

  const types = useCursorList(["product-types", "page"], (cursor) =>
    api.productTypes.list({ cursor, limit: 100 }),
  );

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["product-types"] });

  const create = useMutation({
    mutationFn: () =>
      api.productTypes.create({
        name: form.name,
        has_bom: form.has_bom,
        archive_prefix: form.archive_prefix,
        device_no_regex: form.device_no_regex || null,
        sn_type_code: form.sn_type_code,
      }),
    onSuccess: async () => {
      setForm(EMPTY_FORM);
      setSnCodeEdited(false);
      setCreating(false);
      await invalidate();
    },
  });

  const discontinue = useMutation({
    mutationFn: api.productTypes.discontinue,
    // Discontinuing disables the type's BOMs on the server, so cached BOM lists are stale.
    onSuccess: async () => {
      await invalidate();
      await queryClient.invalidateQueries({ queryKey: ["boms"] });
    },
  });
  const activate = useMutation({ mutationFn: api.productTypes.activate, onSuccess: invalidate });
  const remove = useMutation({
    mutationFn: api.productTypes.remove,
    onSuccess: async () => {
      setPendingDelete(null);
      await invalidate();
    },
  });

  const closeDelete = () => {
    setPendingDelete(null);
    remove.reset();
  };

  // Checked here as well as on the server, because this is the one field on this form
  // where a mistake is permanent: the prefix leads every archive number the type will ever
  // mint, and a product type cannot be edited after creation (US-P-05).
  const prefixEntered = form.archive_prefix.length > 0;
  const prefixValid = ARCHIVE_PREFIX_PATTERN.test(form.archive_prefix);
  // Permanent like the prefix: every SN the type is given carries it.
  const snCodeEntered = form.sn_type_code.length > 0;
  const snCodeValid = SN_TYPE_CODE_PATTERN.test(form.sn_type_code);
  const takenSnCodes = new Set(
    types.items.map((type) => type.sn_type_code).filter((code): code is string => Boolean(code)),
  );
  const canCreate = form.name.trim().length > 0 && prefixValid && snCodeValid && !create.isPending;

  return (
    <section>
      <div className="page-head">
        <h1>{t("productType.title")}</h1>
        {/* Stays mounted while the dialog is open — it is behind an overlay anyway, and
            closing returns focus to the element that was focused on open, which has to
            still exist for that to mean anything. */}
        {canManageMasterData && (
          <button type="button" className="button-primary" onClick={() => setCreating(true)}>
            <AddLabel>{t("common.create")}</AddLabel>
          </button>
        )}
      </div>
      <ErrorBanner error={discontinue.error ?? activate.error} />

      {creating && (
        <Modal
          title={t("productType.createTitle")}
          onClose={() => setCreating(false)}
          // Deliberately no `onSubmit`, so Enter saves nothing and 保存 has to be clicked.
          // Chinese is typed through an IME, where Enter is the key that *commits the
          // candidate* — it is pressed constantly while filling this form, and it would be
          // pressed over a field the operator has not finished thinking about. A product
          // type cannot be edited after creation, so a save landing one keystroke early is
          // not an inconvenience, it is a permanent wrong prefix. Validation that the form
          // element would have done is in `canCreate`.
          footer={
            <>
              <button
                type="button"
                className="button-primary"
                disabled={!canCreate}
                onClick={() => create.mutate()}
              >
                {t("common.save")}
              </button>
              <button type="button" onClick={() => setCreating(false)}>
                {t("common.cancel")}
              </button>
            </>
          }
        >
          <p className="hint">{t("productType.immutableHint")}</p>
          <div className="form-row">
            <label className="required" htmlFor="pt-name">
              {t("productType.name")}
            </label>
            <input
              id="pt-name"
              required
              maxLength={NAME_MAX_LEN}
              value={form.name}
              onChange={(event) => setForm({ ...form, name: event.target.value })}
            />
          </div>
          <div className="form-row">
            <label className="required" htmlFor="pt-prefix">
              {t("productType.archivePrefix")}
            </label>
            <input
              id="pt-prefix"
              required
              className="mono"
              value={form.archive_prefix}
              pattern={ARCHIVE_PREFIX_PATTERN_SOURCE}
              maxLength={8}
              aria-describedby="pt-prefix-hint"
              aria-invalid={prefixEntered && !prefixValid}
              // Upper-cased as it is typed rather than rejected afterwards: the server only
              // accepts upper case, and "rb" is a correct answer typed in the wrong case,
              // not a different answer.
              onChange={(event) => {
                const archive_prefix = event.target.value.toUpperCase();
                setForm({
                  ...form,
                  archive_prefix,
                  sn_type_code: snCodeEdited
                    ? form.sn_type_code
                    : defaultSnTypeCode(archive_prefix, takenSnCodes),
                });
              }}
            />
          </div>
          <div className="form-row">
            <span className="form-row-spacer" />
            <small
              id="pt-prefix-hint"
              className={prefixEntered && !prefixValid ? "hint hint-error" : "hint"}
            >
              {t("productType.archivePrefixHint")}
            </small>
          </div>
          {/* Next to the prefix it is filled in from, and required like it: a type without
              a code would file machines without SNs. */}
          <div className="form-row">
            <label className="required" htmlFor="pt-sn-code">
              {t("robotSn.typeCode")}
            </label>
            <input
              id="pt-sn-code"
              className="mono"
              required
              maxLength={2}
              placeholder="R1"
              pattern="[A-Z0-9]{2}"
              aria-describedby="pt-sn-code-hint"
              aria-invalid={snCodeEntered && !snCodeValid}
              value={form.sn_type_code}
              onChange={(event) => {
                setSnCodeEdited(true);
                setForm({ ...form, sn_type_code: event.target.value.toUpperCase() });
              }}
            />
          </div>
          <div className="form-row">
            <span className="form-row-spacer" />
            <small
              id="pt-sn-code-hint"
              className={snCodeEntered && !snCodeValid ? "hint hint-error" : "hint"}
            >
              {t("robotSn.typeCodeHint")}
            </small>
          </div>
          <div className="form-row">
            <label htmlFor="pt-regex">{t("productType.deviceNoRegex")}</label>
            <input
              id="pt-regex"
              className="mono"
              maxLength={DEVICE_NO_REGEX_MAX_LEN}
              placeholder="^RBT-\\d{4}$"
              value={form.device_no_regex}
              onChange={(event) => setForm({ ...form, device_no_regex: event.target.value })}
            />
          </div>
          <div className="form-row">
            <label htmlFor="pt-bom">{t("productType.hasBom")}</label>
            <input
              id="pt-bom"
              type="checkbox"
              checked={form.has_bom}
              onChange={(event) => setForm({ ...form, has_bom: event.target.checked })}
            />
          </div>
          {/* Inside the dialog, so a rejected save is read where the fields are. */}
          <ErrorBanner error={create.error} />
        </Modal>
      )}

      {pendingDelete && (
        <Modal
          title={t("productType.deleteTitle")}
          onClose={closeDelete}
          onSubmit={() => remove.mutate(pendingDelete.id)}
          footer={
            <>
              <button type="submit" className="button-danger" disabled={remove.isPending}>
                {t("common.delete")}
              </button>
              <button type="button" onClick={closeDelete}>
                {t("common.cancel")}
              </button>
            </>
          }
        >
          {/* Only reachable for a type with no archives — the row greys the button out
              otherwise. Someone may file one while this is open; the server's refusal is
              then read below. */}
          <p>{t("productType.deleteConfirm", { name: pendingDelete.name })}</p>
          {pendingDelete.bom_count > 0 && (
            <p className="hint">{t("productType.deleteBoms", { boms: pendingDelete.bom_count })}</p>
          )}
          {/* A refusal is read here, where the decision is, and the dialog stays open. */}
          <ErrorBanner error={remove.error} />
        </Modal>
      )}

      <ResizableTable storageKey="product-types">
        <thead>
          <tr>
            <th>{t("productType.name")}</th>
            <th>{t("productType.archivePrefix")}</th>
            <th>{t("productType.hasBom")}</th>
            <th>{t("robotSn.typeCode")}</th>
            <th>{t("productType.archiveCount")}</th>
            <th>{t("productType.bomCount")}</th>
            <th>{t("productType.status")}</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {types.items.map((type) => (
            <tr key={type.id}>
              <td>{type.name}</td>
              <td className="mono">{type.archive_prefix}</td>
              <td>{type.has_bom ? t("common.yes") : t("common.no")}</td>
              <td>
                {type.sn_type_code ? (
                  <span className="mono">{type.sn_type_code}</span>
                ) : (
                  <span className="muted">—</span>
                )}
              </td>
              <td>
                {/* A count with somewhere to go. The number answers "is this type in use";
                    the link answers the question that immediately follows it. Zero is not a
                    link, because a filtered list of nothing is a dead end. */}
                {type.archive_count > 0 ? (
                  <Link to={`/archives?product_type_id=${type.id}`}>{type.archive_count}</Link>
                ) : (
                  <span className="muted">0</span>
                )}
              </td>
              <td>
                {type.bom_count > 0 ? (
                  <Link to={`/boms?product_type_id=${type.id}`}>{type.bom_count}</Link>
                ) : (
                  <span className="muted">0</span>
                )}
              </td>
              <td>
                <StatusBadge status={type.status} />
              </td>
              <td className="row-actions">
                {/* One slot for the status toggle, so the column holds still: the icon says
                    which way it goes. */}
                {!canManageMasterData ? null : type.status === "ACTIVE" ? (
                  <IconButton
                    label={t("productType.discontinue")}
                    hint={t("productType.discontinueHint")}
                    icon={<BanIcon />}
                    disabled={discontinue.isPending}
                    onClick={() => discontinue.mutate(type.id)}
                  />
                ) : (
                  <IconButton
                    label={t("productType.activate")}
                    icon={<RestoreIcon />}
                    disabled={activate.isPending}
                    onClick={() => activate.mutate(type.id)}
                  />
                )}
                {canManageMasterData && (
                  <IconButton
                    label={t("common.delete")}
                    icon={<TrashIcon />}
                    danger
                    disabled={type.archive_count > 0}
                    disabledReason={t("productType.deleteBlocked", {
                      name: type.name,
                      archives: type.archive_count,
                    })}
                    onClick={() => setPendingDelete(type)}
                  />
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </ResizableTable>
      <Pager
        hasNextPage={types.hasNextPage}
        isFetchingNextPage={types.isFetchingNextPage}
        onMore={() => void types.fetchNextPage()}
      />
    </section>
  );
}
